import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import {
    Toolbar,
    LazyBreadcrumb,
    CenterCircularProgress,
    NotFound,
    BooleanBadge,
    useModal,
} from '@europrocurement/flexy-components';
import { Container } from '@mui/system';

import {
    GroupesDeTravailJsonld as GroupeTravailDTO,
    GroupesDeTravailJsonldUtilisateurReadDomaineRead as GroupeTravailRead,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import {
    groupesTravailSelectorMain,
    groupeTravailActiveInscriptionsSelector,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';

import { enqueueSnackbar } from 'notistack';
import { ValidationModal } from '@europrocurement/l2d-modules/modules/ValidationModal/ValidationModal';
import { utilisateursGroupesTravailPath } from '../../constants/paths';
import { WorkGroupContainer } from '../../components/GroupesTravail/WorkGroupContainer';
import { WorkGroupHeader } from '../../components/GroupesTravail/WorkGroupHeader/WorkGroupHeader';

// Types
type GroupData = GroupeTravailRead;

// Component
const GroupeTravailFiche: React.FunctionComponent = function () {
    // Get hooks
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();

    // Get selector and thunk
    const dataSource = useUtilisateursServiceSelector(groupesTravailSelectorMain);
    const inscriptionsDataSource = useUtilisateursServiceSelector(
        groupeTravailActiveInscriptionsSelector,
    );
    const { selectGroupeTravail, getGroupeTravailActiveInscriptions, groupesTravailApi } =
        useUtilisateurService();

    /**
     * Workgroup part
     */
    // Get groupe travail id
    const { idGroupeTravail } = useParams();

    // Create groupe de travail state
    const [groupData, setGroupData] = useState<GroupData>();
    const [isActiveStatus, setIsActiveStatus] = useState<boolean>(false);
    const workgroup = dataSource.selected;

    // Fetch workgroup data.
    useEffect(() => {
        if (idGroupeTravail) {
            // unselect to remove the previous workgroup's data
            dispatch({ type: 'groupeTravail/deletemainSelected' });

            // Fetch the current workgroup (same as the route)
            dispatch(selectGroupeTravail({ id: idGroupeTravail }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idGroupeTravail]);

    // Check request status.
    useEffect(() => {
        const groupLoaded = idGroupeTravail && workgroup && `${workgroup.id}` === idGroupeTravail;

        const dataSourceLoaded = dataSource.selectedStatus === 'succeeded';

        // if the user is the right one and his data have been retrieved, put them to the state
        if (groupLoaded && dataSourceLoaded) {
            setGroupData(workgroup);
        }
    }, [dataSource, idGroupeTravail, workgroup]);

    // Define isActiveStatus (true = active)
    useEffect(() => {
        if (!workgroup) return;

        if (workgroup.fDelete === false) {
            if (workgroup.dCloture !== undefined && workgroup.dCloture !== null) {
                // Check if end date is passed
                const today: Date = new Date();
                const dateCloture: Date = new Date(workgroup.dCloture);
                const status = dateCloture > today;

                setIsActiveStatus(status);
            } else if (workgroup?.dCloture === null) {
                setIsActiveStatus(true);
            } else {
                setIsActiveStatus(false);
            }
        } else {
            setIsActiveStatus(false);
        }
    }, [workgroup, idGroupeTravail, dataSource]);

    /**
     * Fetch Inscriptions part
     */
    // Set filters to prepare fetch active inscriptions
    useEffect(() => {
        if (dataSource.selected?.id) {
            // Set filters to fetch active only inscriptions full list
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Pagination`,
                payload: { page: 0, itemsPerPage: 100 },
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Filter`,
                payload: { key: 'historicInscription', value: false },
            });
            dispatch({
                type: `${inscriptionsDataSource.slicename}/set${inscriptionsDataSource.name}Filter`,
                payload: { key: 'groupetravail', value: dataSource.selected.id },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource.selected]);

    // Listen to filters, check them, and fetch if ready
    useEffect(() => {
        let idGroupeTravailInt;
        // Parse for comparing
        if (idGroupeTravail !== undefined) {
            idGroupeTravailInt = parseInt(idGroupeTravail, 10);
        }

        if (
            dataSource.selected?.id === idGroupeTravailInt &&
            inscriptionsDataSource.filters.groupetravail === dataSource.selected?.id &&
            inscriptionsDataSource.filters.historicInscription === false &&
            inscriptionsDataSource.pagination.itemsPerPage === 100 &&
            inscriptionsDataSource.pagination.page === 0
        ) {
            dispatch(getGroupeTravailActiveInscriptions({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inscriptionsDataSource.filters.groupetravail, dataSource.selected?.members]);

    /**
     * Error and loader part
     */
    if (!idGroupeTravail) {
        return null;
    }
    if (dataSource.selectedStatus === 'failed') {
        return <NotFound />;
    }
    if (!groupData || !workgroup || isActiveStatus === undefined) {
        return <CenterCircularProgress sx={{ height: '70vh' }} />;
    }

    /**
     * Worgroup status update part
     */
    const updateStatus = () => {
        // Request to re-activate workgroup (now)
        if (isActiveStatus === false) {
            // Format workgroup DTO.
            const updatedWorkgroup: GroupeTravailDTO = {
                idGroupeTravail: workgroup['@id'],
                lib: workgroup.lib,
                domaine: workgroup.domaine?.['@id'],
                iduserReferent: workgroup.iduserReferent?.['@id'],
                fDelete: false, // change flag to false here for backend control
                phone: workgroup.phone,
                email: workgroup.email,
                dCreation: workgroup.dCreation,
                dCloture: workgroup.dCloture,
                inscriptions: [],
            };

            const activateWorkGroup = () => {
                groupesTravailApi
                    .apiGroupestravailIdGroupeTravailStatusPatch({
                        idGroupeTravail: `${workgroup.id}`,
                        status: 'activate',
                        groupesDeTravail: updatedWorkgroup,
                    })
                    .then(() => {
                        // Change state
                        setIsActiveStatus(true);
                        // Close form modal.
                        modalActions.close();
                        // Add notification to user.
                        enqueueSnackbar('Groupe de travail activé avec succès!', {
                            variant: 'success',
                        });
                        // Update store.
                        dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                    })
                    .catch((error) => {
                        console.error(error);
                        const errorMessage = error.response?.data?.exception?.message;
                        enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                            variant: 'error',
                        });
                    });
            };
            activateWorkGroup();
        }

        // Request to de-activate workgroup (check no remaining active inscription)
        if (isActiveStatus === true) {
            const closeWorkGroup = () => {
                groupesTravailApi
                    .apiGroupestravailIdDelete({
                        id: `${workgroup.id}`,
                    })
                    .then(() => {
                        // Change state
                        setIsActiveStatus(false);
                        // Close form modal.
                        modalActions.close();
                        // Add notification to user.
                        enqueueSnackbar('Groupe de travail clôturé avec succès!', {
                            variant: 'success',
                        });
                        // Update store.
                        dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                    })
                    .catch((error) => {
                        console.error(error);
                        const errorMessage = error.response?.data?.exception?.message;
                        enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                            variant: 'error',
                        });
                    });
            };
            closeWorkGroup();
        }
    };

    // Workgroup active status change validation modal.
    const handleChangeActiveStatus = () => {
        const validationSentence =
            'Êtes-vous sûr de vouloir clôturer ce groupe de travail ? Il doit impérativement avoir une date de fin.';

        const message = isActiveStatus
            ? validationSentence
            : 'Êtes-vous sûr de vouloir activer ce groupe de travail ?';

        modalActions.call(
            <ValidationModal
                message={message}
                onSubmit={() => {
                    updateStatus();
                }}
            />,
        );
    };

    /**
     * Components part
     */
    const breadcrumbPath = [
        {
            path: 'Groupes de Travail',
            link: `${utilisateursGroupesTravailPath}`,
        },
        `${workgroup?.lib}`,
        '',
    ];

    return (
        <Container maxWidth="lg">
            <Toolbar previousPath={utilisateursGroupesTravailPath}>
                <LazyBreadcrumb path={breadcrumbPath} />

                <BooleanBadge
                    label="Groupe actif"
                    value={isActiveStatus}
                    icon
                    toggleAction={() => {
                        handleChangeActiveStatus();
                    }}
                />
            </Toolbar>

            <WorkGroupHeader
                workgroup={workgroup}
                isActiveStatus={isActiveStatus}
            />

            <WorkGroupContainer
                workgroup={workgroup}
                isActiveStatus={isActiveStatus}
            />
        </Container>
    );
};

export default GroupeTravailFiche;
